import DOMPurify from "dompurify";
import CryptoJS from "crypto-js";
import config from "./config";
import { LoggedInAccount } from "./Interfaces/AccountUsers";
import fetchWithAuth from "./services/fetchWithAuth";

const SECRET_KEY = config.QUERY_ENCODE_DECODE_SECRET_KEY;
const API_LINK = config.API_BASE_URL;
export const DEFAULT_BACKGROUND_COLOR = "#FFFFFF";
export const DEFAULT_TEXT_COLOR = "#000000";

// remove for now because of key error
// if (typeof SECRET_KEY !== "string") {
//   throw new Error("No Secret Key defined");
// }

export class InvalidFundraiserKey extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidFundraiserKey";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export class InvalidParticipantKey extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidParticipantKey";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export const isStringNullOrEmpty = (str?: string | null): boolean => {
  return str === undefined || str === null || str.trim() === "";
};

export const sanitizeTextInput = (input: string): string => {
  return DOMPurify.sanitize(input);
};

export const sanitizeEmailInput = (input: string): string => {
  const sanitizedInput = DOMPurify.sanitize(input);
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (emailPattern.test(sanitizedInput)) {
    return sanitizedInput;
  } else {
    throw new Error("Invalid email address");
  }
};

export const sanitizePhoneNumber = (input: string): string => {
  // Remove all non-numeric characters except '+' (for country code)
  input = input.replace(/[^\d+]/g, "").trim();

  // Regular expression to match valid phone numbers
  const phoneNumberPattern = /^(\+\d{1,3}[- ]?)?(\d{10,15})$/;

  if (input === "" || !phoneNumberPattern.test(input)) {
    throw new Error("Invalid phone number");
  }

  return input;
};

export const encodeQueryParameter = (
  params: Record<string, string>,
): string => {
  if (typeof SECRET_KEY !== "string") {
    throw new Error("No Secret Key defined");
  }

  // Convert the parameters object to a JSON string
  const paramsString = JSON.stringify(params);

  // Generate a random IV (16 bytes)
  const iv = CryptoJS.lib.WordArray.random(16);

  // Encrypt the parameters string using CBC mode
  const encrypted = CryptoJS.AES.encrypt(
    paramsString,
    CryptoJS.enc.Utf8.parse(SECRET_KEY),
    {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    },
  );

  // Concatenate IV (Base64) + Ciphertext (Base64)
  let encryptedParam = CryptoJS.enc.Base64.stringify(
    iv.concat(encrypted.ciphertext),
  );

  // Convert to URL-safe base64 to match backend encoding
  encryptedParam = encryptedParam.replace(/\+/g, "-").replace(/\//g, "_");

  // Encode for URL transport
  return encodeURIComponent(encryptedParam);
};

export const decodeQueryParameter = (
  string_to_decode: string,
): Record<string, string> => {
  // Decode from URL encoding and normalize URL-safe base64 chars to standard base64
  const encryptedParam = decodeURIComponent(string_to_decode)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  // Decode the Base64-encoded string to get raw bytes
  const encryptedBytes = CryptoJS.enc.Base64.parse(encryptedParam);

  // Extract IV (first 16 bytes) and encrypted data (remaining bytes)
  const iv = CryptoJS.lib.WordArray.create(
    encryptedBytes.words.slice(0, 4),
    16,
  );
  const encryptedData = CryptoJS.lib.WordArray.create(
    encryptedBytes.words.slice(4),
  );

  // Check for secret key validity
  if (typeof SECRET_KEY !== "string") {
    throw new Error("No Secret Key defined");
  }

  try {
    // Decrypt the data using AES-CBC mode with the extracted IV
    const decryptedBytes = CryptoJS.AES.decrypt(
      {
        ciphertext: encryptedData, // Correct way to provide ciphertext
      } as CryptoJS.lib.CipherParams, // Properly casting to CipherParams type
      CryptoJS.enc.Utf8.parse(SECRET_KEY),
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      },
    );

    // Convert decrypted data to string
    const decryptedParamString = decryptedBytes.toString(CryptoJS.enc.Utf8);
    if (!decryptedParamString) {
      throw new Error("Failed to decrypt");
    }

    // Parse the decrypted string to a JSON object
    const params = JSON.parse(decryptedParamString);
    return params;
  } catch (error) {
    console.error("Error during decryption:", error);
    return { error: "decryption_failed" };
  }
};

export const parseFundraiserID = (fundraiser_key: string): number => {
  // Ensure the key is at least 7 characters long
  if (fundraiser_key.length < 7) {
    throw new InvalidFundraiserKey(
      "An invalid fundraiser key was provided: too short",
    );
  }

  // Slice the key and extract the part after the first 7 characters
  const slicedPart = fundraiser_key.slice(7);

  // Check if the sliced part is empty
  if (!slicedPart) {
    throw new InvalidFundraiserKey(
      "An invalid fundraiser key was provided: empty ID",
    );
  }

  // Use regex to ensure the sliced part contains only numeric characters
  if (!/^\d+$/.test(slicedPart)) {
    throw new InvalidFundraiserKey(
      "An invalid fundraiser key was provided: non-numeric value",
    );
  }

  // Convert to number and return
  return Number(slicedPart);
};

export const parseParticipantID = (participant_key: string): number => {
  // Ensure the key is at least 7 characters long
  if (participant_key.length < 7) {
    throw new InvalidFundraiserKey(
      "An invalid participant key was provided: too short",
    );
  }

  // Slice the key and extract the part after the first 7 characters
  const slicedPart = participant_key.slice(4);

  // Check if the sliced part is empty
  if (!slicedPart) {
    throw new InvalidFundraiserKey(
      "An invalid participant key was provided: empty ID",
    );
  }

  // Use regex to ensure the sliced part contains only numeric characters
  if (!/^\d+$/.test(slicedPart)) {
    throw new InvalidFundraiserKey(
      "An invalid participant key was provided: non-numeric value",
    );
  }

  // Convert to number and return
  return Number(slicedPart);
};

export const SHIP_TO_ORG = "Ship to Organization";
export const SHIP_TO_PARTICIPANT = "Ship to Participant";
export const SHIP_TO_CUSTOMER = "Ship to Customer";

export const stateAbbreviations: string[] = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

// TODO: NOTE: these Org categories are temporarily removed until the frontend supports them
export const productCategories: [string, string][] = [
  ["40oz_bottle", "Custom 40oz Bottles"],
  // ["40oz_org_bottle", "Organization 40oz Bottles"],
  ["20oz_bottle", "Custom 20oz Bottles"],
  // ["20oz_org_bottle", "Organization 20oz Bottles"],
  ["tumbler", "Custom Tumblers"],
  // ["org_tumbler", "Organization Tumblers"],
  ["travel_mug", "Custom Travel Mugs"],
  // ["org_travel_mug", "Organization Travel Mugs"],
  ["candy", "Candies"],
  ["chocolate", "Chocolates"],
  ["cookie", "Cookies"],
  ["gift", "Gifts"],
  ["nut", "Nuts"],
  ["popcorn", "Popcorn"],
  ["hoodie", "Custom Hoodies"],
  ["shirt", "Custom T-Shirts"],
  ["racerback", "Custom Racerbacks"],
  ["bear", "Bears"],
  ["jerky", "Jerky"],
];

export const getCurrentlyLoggedInUser = async (): Promise<LoggedInAccount> => {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) {
    throw new Error("No access token found in localStorage.");
  }

  try {
    const response = await fetchWithAuth(`${API_LINK}account/user-from-token`);

    if (response.status === 401) {
      throw new Error("User is not signed in.");
    }

    if (response.ok) {
      const data = await response.json();
      return {
        id: data.account_id,
        email: data.email,
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        profile_pic: data.profile_pic || "",
        account_profiles: data.account_profiles || [],
        is_staff: data.is_staff || false,
      };
    } else {
      const errorData = await response.json();
      throw new Error(errorData.detail || "Failed to fetch user details.");
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : "An unknown error occurred while fetching user details.";
    throw new Error(errorMessage);
  }
};

export const logoutUser = async () => {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) {
    throw new Error("No access token found in localStorage.");
  }
  try {
    const response = await fetch(`${API_LINK}logout/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      localStorage.removeItem("accessToken");
      return response;
    } else {
      const errorData = await response.json();
      throw new Error(errorData.detail || "Failed to logout user.");
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : "An unknown error occurred while logging out user.";
    throw new Error(errorMessage);
  }
};

export const formatPossessive = (name: string): string => {
  return name.endsWith("s") ? `${name}'` : `${name}'s`;
};

export const isLightBackground = (hexColor: string): boolean => {
  // https://stackoverflow.com/questions/3942878/how-to-decide-font-color-in-white-or-black-depending-on-background-color
  const color = hexColor.replace("#", "");

  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);

  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  return brightness > 128;
};

export const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;
export const formatCurrencyWithCommas = (amount: number) => {
  return formatCurrency(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const formatDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

export const colorHexMap: Record<string, string> = {
  Black: "#000000",
  Gray: "#6c757d",
  Grey: "#6c757d",
  Navy: "#191f2b",
  Red: "#ff0000",
  Royal: "#4169e1",
  White: "#dddddd",
  Burgundy: "#80426b", // extra colors based on bottles, just in case
  Green: "#005c30",
  Purple: "#a179ae",
  "Light Blue": "#90d4e9",
  Pink: "#f064a5",
};

// Define enums for categories and requirement fields
export enum Category {
  Custom40ozBottles = "Custom 40oz Bottles",
  Organization40ozBottles = "Organization 40oz Bottles",
  Custom20ozBottles = "Custom 20oz Bottles",
  Organization20ozBottles = "Organization 20oz Bottles",
  CustomTumblers = "Custom Tumblers",
  OrganizationTumblers = "Organization Tumblers",
  CustomTravelMugs = "Custom Travel Mugs",
  OrganizationTravelMugs = "Organization Travel Mugs",
  CustomHoodies = "Custom Hoodies",
  CustomTShirts = "Custom T-Shirts",
  CustomRacerbacks = "Custom Racerbacks",
  Default = "Default",
}

export enum Requirement {
  Style = "style",
  Color = "color",
  Size = "size",
  ClothingCustomization = "clothing_customization",
  CustomizationText = "customization_text",
  CustomTextFieldActive = "custom_text_field_active",
  FontStyle = "font_style",
  Quantity = "quantity",
}

const clothingRequirements: Requirement[] = [
  Requirement.Style,
  Requirement.Color,
  Requirement.Size,
  Requirement.ClothingCustomization,
  Requirement.CustomTextFieldActive,
  Requirement.CustomizationText,
  Requirement.Quantity,
];

const bottleRequirements: Requirement[] = [
  Requirement.Style,
  Requirement.Color,
  Requirement.CustomizationText,
  Requirement.CustomTextFieldActive,
  Requirement.FontStyle,
  Requirement.Quantity,
];

// Map category to requirement arrays
export const categoryOrderRequirements: Record<Category, Requirement[]> = {
  [Category.Custom40ozBottles]: bottleRequirements,
  [Category.Organization40ozBottles]: bottleRequirements,
  [Category.Custom20ozBottles]: bottleRequirements,
  [Category.Organization20ozBottles]: bottleRequirements,
  [Category.CustomTumblers]: bottleRequirements,
  [Category.OrganizationTumblers]: bottleRequirements,
  [Category.CustomTravelMugs]: bottleRequirements,
  [Category.OrganizationTravelMugs]: bottleRequirements,
  [Category.CustomHoodies]: clothingRequirements,
  [Category.CustomTShirts]: clothingRequirements,
  [Category.CustomRacerbacks]: clothingRequirements,
  [Category.Default]: [Requirement.Quantity],
};

// Helper function to fetch requirements by category
export const getCategoryRequirements = (category: Category): Requirement[] => {
  return (
    categoryOrderRequirements[category] ??
    categoryOrderRequirements[Category.Default]
  );
};

export const parseBaseShippingOverrides = (
  baseShippingOverride: string,
): Record<string, number> => {
  try {
    return JSON.parse(baseShippingOverride);
  } catch {
    return {};
  }
};

export const parseFreeShippingExceptions = (
  freeShippingExceptions: string,
): Set<string> => {
  return new Set(
    freeShippingExceptions
      .split(",")
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean),
  );
};

export const calculateShippingCost = (
  stateInitials: string,
  itemCount: number,
  freeShippingThreshold: number,
  baseShipping: number,
  currentCost: number,
  freeShippingExceptions: string,
  baseShippingOverride: string,
  shippingChoice: string,
) => {
  const parsedBaseShippingOverride =
    parseBaseShippingOverrides(baseShippingOverride);
  const parsedFreeShippingExceptions = parseFreeShippingExceptions(
    freeShippingExceptions,
  );

  if (
    (currentCost >= freeShippingThreshold || shippingChoice === SHIP_TO_ORG) &&
    !parsedFreeShippingExceptions.has(stateInitials)
  ) {
    return 0.0;
  }

  const baseShippingForState =
    parsedBaseShippingOverride[stateInitials] ?? baseShipping;
  return Number(baseShippingForState) + itemCount;
};

export const slugify = (text: string) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
};

export const replaceCharacterWithSpace = (text: string, character: string) =>
  text.replace(new RegExp(character, "g"), " ");

export const convertToTitleCase = (text: string): string => {
  return text
    .toLocaleLowerCase()
    .replace(/\b\w/g, (char) => char.toLocaleUpperCase());
};
